import React from "react";
import { useSrsLanguage } from "../components/LanguageSwitch";
import { Accordion, Button, Card, Form, Nav, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Clipboard, Locale, PBToken, Token } from "../utils";
import { useErrorHandler } from "react-error-boundary";
import { useSearchParams } from "react-router-dom";
import { buildUrls } from "../components/UrlGenerator";
import { SrsEnvContext } from "../components/SrsEnvContext";
import * as Icon from "react-bootstrap-icons";
import PopoverConfirm from "../components/PopoverConfirm";
import { OpenAISecretSettings } from "../components/OpenAISettings";

// PocketBase 登录初始化
const PB_USERNAME = process.env.REACT_APP_PB_USERNAME;
const PB_PASSWORD = process.env.REACT_APP_PB_PASSWORD;
// console.log('PB Login: ', PB_USERNAME, PB_PASSWORD);
if (PB_USERNAME && PB_PASSWORD) {
  PBToken.login(PB_USERNAME, PB_PASSWORD);
}

// 生成 8 位随机字符串（大小写字母 + 数字）
const generateRandomPasswd = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

export default function ScenarioLiveRoom() {
  const [searchParams] = useSearchParams();
  // The room id, to maintain a specified room.
  const [roomId, setRoomId] = React.useState();

  React.useEffect(() => {
    const id = searchParams.get('roomid') || null;
    console.log(`?roomid=xxx, current=${id}, Set the roomid to manage.`);
    setRoomId(id);
  }, [searchParams, setRoomId]);

  return <>{!roomId && <ScenarioLiveRoomList {...{ setRoomId }} />}
    {roomId && <ScenarioLiveRoomImpl {...{ setRoomId, roomId }} />}</>;
}

function ScenarioLiveRoomList({ setRoomId }) {
  const language = useSrsLanguage();
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const [searchParams, setSearchParams] = useSearchParams();
  const [name, setName] = React.useState('My Live Room');
  const [rooms, setRooms] = React.useState([]);
  const [refreshNow, setRefreshNow] = React.useState();

  const createLiveRoom = React.useCallback((e) => {
    e.preventDefault();

    axios.post('/terraform/v1/live/room/create', {
      title: name,
    }, {
      headers: Token.loadBearerHeader(),
    }).then(res => {
      const roomData = res.data.data;
      const { uuid, stream, title } = roomData;
      // 生成随机 passwd
      const passwd = generateRandomPasswd();

      // 插入数据到 PB
      axios.post('/api/collections/live_rooms/records', {
        title: title,
        stream: stream,
        passwd: passwd,
      }, {
        headers: PBToken.loadBearerHeader(),
      }).then(pbResult => {
        console.log(`PB: Create ok, data=${JSON.stringify(pbResult)}`);
      }).catch(pbErr => {
        console.error(`PB: Create error, ${pbErr}`);
      });

      searchParams.set('roomid', uuid); setSearchParams(searchParams);
      setRoomId(uuid);
      console.log(`Status: Create ok, name=${name}, data=${JSON.stringify(roomData)}`);
    }).catch(handleError);
  }, [handleError, name, setRoomId, searchParams, setSearchParams]);

  const removeRoom = React.useCallback((uuid) => {
    // 找到对应的 room 获取 stream
    const roomToRemove = rooms.find(r => r.uuid === uuid);
    const stream = roomToRemove?.stream;

    axios.post('/terraform/v1/live/room/remove', {
      uuid: uuid,
    }, {
      headers: Token.loadBearerHeader(),
    }).then(res => {
      // 删除 PB 中对应的记录
      if (stream) {
        axios.get('/api/collections/live_rooms/records', {
          params: { filter: `stream="${stream}"` },
          headers: PBToken.loadBearerHeader(),
        }).then(recordRes => {
          const record = recordRes.data.items?.[0];
          if (record) {
            axios.delete(`/api/collections/live_rooms/records/${record.id}`, {
              headers: PBToken.loadBearerHeader(),
            }).then(() => {
              console.log(`PB: Delete ok, stream=${stream}`);
            }).catch(pbErr => {
              console.error(`PB: Delete error, ${pbErr}`);
            });
          }
        }).catch(pbErr => {
          console.error(`PB: Query for delete error, ${pbErr}`);
        });
      }
      setRefreshNow(!refreshNow);
      console.log(`Status: Remove ok, uuid=${uuid}, data=${JSON.stringify(res.data.data)}`);
    }).catch(handleError);
  }, [handleError, refreshNow, setRefreshNow, rooms]);

  const manageRoom = React.useCallback((room) => {
    const uuid = room.uuid;
    searchParams.set('roomid', uuid); setSearchParams(searchParams);
    setRoomId(room.uuid);
  }, [searchParams, setSearchParams, setRoomId]);

  const copyRoom = React.useCallback(async (roomCopy) => {
    const name = `Copy of ${roomCopy.title}`;
    // 生成随机 passwd
    const passwd = generateRandomPasswd();

    const room = await new Promise(resolve => {
      axios.post('/terraform/v1/live/room/create', {
        title: name,
      }, {
        headers: Token.loadBearerHeader(),
      }).then(res => {
        const room = res.data.data;
        console.log(`Status: Create ok, name=${name}, data=${JSON.stringify(res.data.data)}`);

        // 插入数据到 PB
        axios.post('/api/collections/live_rooms/records', {
          title: room.title,
          stream: room.stream,
          passwd: passwd,
        }, {
          headers: PBToken.loadBearerHeader(),
        }).then(pbResult => {
          console.log(`PB: Create ok, data=${JSON.stringify(pbResult)}`);
        }).catch(pbErr => {
          console.error(`PB: Create error, ${pbErr}`);
        });

        resolve(room);
      }).catch(handleError);
    });

    await new Promise(resolve => {
      axios.post('/terraform/v1/live/room/update', {
        ...roomCopy,
        // Do not copy the stream, secret, and token.
        uuid: room.uuid, title: room.title, stream: room.stream, secret: room.secret,
        roomToken: room.roomToken, created_at: room.created_at,
        // Avoid copying the stage uuid, as there should be no stage for the new room.
        stage_uuid: '',
      }, {
        headers: Token.loadBearerHeader(),
      }).then(res => {
        alert(t('helper.setOk'));
        console.log(`Room: Update ok, uuid=${room.uuid}, data=${JSON.stringify(res.data.data)}`);

        // 同步更新 PB 中的 title
        if (res.data.data.stream) {
          axios.get('/api/collections/live_rooms/records', {
            params: { filter: `stream="${res.data.data.stream}"` },
            headers: PBToken.loadBearerHeader(),
          }).then(recordRes => {
            const record = recordRes.data.items?.[0];
            if (record) {
              axios.patch(`/api/collections/live_rooms/records/${record.id}`, {
                title: res.data.data.title,
              }, {
                headers: PBToken.loadBearerHeader(),
              }).then(() => {
                console.log(`PB: Update title ok, stream=${res.data.data.stream}, title=${res.data.data.title}`);
              }).catch(pbErr => {
                console.error(`PB: Update title error, ${pbErr}`);
              });
            }
          }).catch(pbErr => {
            console.error(`PB: Query for update error, ${pbErr}`);
          });
        }

        resolve();
      }).catch(handleError);
    });
  }, [handleError, t]);

  React.useEffect(() => {
    const refreshLiveRoomsTask = () => {
      // 先获取所有 PB 记录
      axios.get('/api/collections/live_rooms/records', {
        params: { page: 1, perPage: 999 },
        headers: PBToken.loadBearerHeader(),
      }).then(pbResult => {
        const pbRecords = pbResult.data?.items || [];
        // 创建一个 Map 以 stream 为 key 快速查找
        const pbMap = {};
        pbRecords.forEach(record => {
          pbMap[record.stream] = record;
        });
        // console.log(`PB: List all rooms, ${pbRecords}`);

        // 获取后端房间列表
        axios.post('/terraform/v1/live/room/list', {
        }, {
          headers: Token.loadBearerHeader(),
        }).then(res => {
          const { rooms } = res.data.data;

          // 将后端数据与 PB 数据合并
          const mergedRooms = (rooms || []).map(room => {
            const pbRecord = pbMap[room.stream];
            return { ...room, passwd: pbRecord?.passwd || '-' };
          });

          setRooms(mergedRooms.sort((a, b) => {
            if (a.created_at === b.created_at) return a.uuid > b.uuid ? -1 : 1;
            return a.created_at > b.created_at ? -1 : 1;
          }));
          console.log(`Status: List ok with PB, data=${JSON.stringify(mergedRooms)}`);
        }).catch(handleError);
      }).catch(pbErr => {
        console.error(`PB: Query all error, ${pbErr}`);
        // 即使 PB 查询失败，仍然显示后端房间列表
        axios.post('/terraform/v1/live/room/list', {
        }, {
          headers: Token.loadBearerHeader(),
        }).then(res => {
          const { rooms } = res.data.data;
          setRooms((rooms || []).map(room => ({ ...room, passwd: '-' })));
        }).catch(handleError);
      });
    };

    refreshLiveRoomsTask();
    const timer = setInterval(() => refreshLiveRoomsTask(), 3 * 1000);
    return () => {
      clearInterval(timer);
      setRooms([]);
    }
  }, [handleError, setRooms, refreshNow]);

  return (
    <Accordion defaultActiveKey={['1', '2']}>
      <React.Fragment>
        {language === 'zh' ?
          <Accordion.Item eventKey="0">
            <Accordion.Header>场景介绍</Accordion.Header>
            <Accordion.Body>
              <div>直播间，提供了按每个流鉴权的能力，并支持直播间的业务功能。</div>
              <p></p>
              <p>可应用的具体场景包括：</p>
              <ul>
                <li>自建直播间，私域直播，仅限私域会员能观看的直播。</li>
                <li>企业直播，企业内部的直播间，仅限企业内部人员观看。</li>
                <li>电商直播，仅限电商特定买家可观看的直播。</li>
              </ul>
            </Accordion.Body>
          </Accordion.Item> :
          <Accordion.Item eventKey="0">
            <Accordion.Header>Scenario Introduction</Accordion.Header>
            <Accordion.Body>
              <div>Live room, which provides the ability to authenticate each stream and supports business functions of live room.</div>
              <p></p>
              <p>The specific scenarios that can be applied include:</p>
              <ul>
                <li>Self-built live room, private domain live broadcast, live broadcast that can only be watched by private domain members.</li>
                <li>Enterprise live broadcast, live room within the enterprise, only for internal personnel of the enterprise.</li>
                <li>E-commerce live broadcast, live broadcast that can only be watched by specific buyers of e-commerce.</li>
              </ul>
            </Accordion.Body>
          </Accordion.Item>}
      </React.Fragment>
      <Accordion.Item eventKey="1">
        <Accordion.Header>{t('lr.create.title')}</Accordion.Header>
        <Accordion.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t('lr.create.name')}</Form.Label>
              <Form.Text> * {t('lr.create.name2')}</Form.Text>
              <Form.Control as="input" defaultValue={name} onChange={(e) => setName(e.target.value)} />
            </Form.Group>
            <Button ariant="primary" type="submit" onClick={(e) => createLiveRoom(e)}>
              {t('helper.create')}
            </Button>
          </Form>
        </Accordion.Body>
      </Accordion.Item>
      <Accordion.Item eventKey="2">
        <Accordion.Header>{t('lr.list.title')}</Accordion.Header>
        <Accordion.Body>
          {rooms?.length ? <Table striped bordered hover>
            <thead>
              <tr>
                <th>#</th>
                <th>UUID</th>
                <th>Title</th>
                <th>Stream</th>
                <th>Passwd</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rooms?.map((room, index) => {
                return <tr key={room.uuid}>
                  <td>{index}</td>
                  <td>
                    <a href="#!" onClick={(e) => {
                      e.preventDefault();
                      manageRoom(room);
                    }}>{room.uuid}</a>
                  </td>
                  <td>{room.title}</td>
                  <td>{room.stream}</td>
                  <td>{room.passwd || '-'}</td>
                  <td>{room.created_at}</td>
                  <td>
                    <a href="#!" onClick={(e) => {
                      e.preventDefault();
                      manageRoom(room);
                    }}>{t('helper.manage')}</a> &nbsp;
                    <PopoverConfirm placement='top' trigger={<a href='#!'>{t('helper.delete')}</a>} onClick={() => removeRoom(room.uuid)}>
                      <p>{t('lr.list.delete')}</p>
                    </PopoverConfirm> &nbsp;
                    <PopoverConfirm placement='top' trigger={<a href='#!'>{t('helper.copy')}</a>} onClick={() => copyRoom(room)}>
                      <p>{t('lr.list.copy')}</p>
                    </PopoverConfirm>
                  </td>
                </tr>;
              })}
            </tbody>
          </Table> : t('lr.list.empty')}
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
}

function ScenarioLiveRoomImpl({ roomId, setRoomId }) {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const [requesting, setRequesting] = React.useState(false);
  const [room, setRoom] = React.useState();
  const [searchParams, setSearchParams] = useSearchParams();

  React.useEffect(() => {
    const fetchRoom = async () => {
      try {
        const res = await axios.post('/terraform/v1/live/room/query', {
          uuid: roomId,
        }, {
          headers: Token.loadBearerHeader(),
        });
        const qureyRoom = res.data.data;
        // 等待 PB 查询完成后再设置 room
        if (qureyRoom.stream) {
          try {
            const recordRes = await axios.get('/api/collections/live_rooms/records', {
              params: { filter: `stream="${qureyRoom.stream}"` },
              headers: PBToken.loadBearerHeader(),
            });
            const record = recordRes.data.items?.[0];
            if (record) {
              qureyRoom.passwd = record.passwd;
            }
          } catch (pbErr) {
            console.error(`PB: Query error, ${pbErr}`);
          }
        }
        setRoom(qureyRoom);
        console.log(`Room: Query ok with passwd, uuid=${roomId}, data=${JSON.stringify(qureyRoom)}`);
      } catch (error) {
        handleError(error);
      }
    };

    fetchRoom();
  }, [handleError, roomId, setRoom]);

  const updateRoom = React.useCallback(async (room) => {
    setRequesting(true);
    try {
      const res = await axios.post('/terraform/v1/live/room/update', {
        uuid: room.uuid, ...room,
      }, {
        headers: Token.loadBearerHeader(),
      });
      const updatedRoom = res.data.data;
      // 等待 PB 查询和更新完成后再设置 room
      if (updatedRoom.stream) {
        try {
          const recordRes = await axios.get('/api/collections/live_rooms/records', {
            params: { filter: `stream="${updatedRoom.stream}"` },
            headers: PBToken.loadBearerHeader(),
          });
          const record = recordRes.data.items?.[0];
          if (record) {
            updatedRoom.passwd = record.passwd;
            await axios.patch(`/api/collections/live_rooms/records/${record.id}`, {
              title: updatedRoom.title,
            }, {
              headers: PBToken.loadBearerHeader(),
            });
            console.log(`PB: Update title ok, stream=${updatedRoom.stream}, title=${updatedRoom.title}`);
          }
        } catch (pbErr) {
          console.error(`PB: Update error, ${pbErr}`);
        }
      }

      alert(t('helper.setOk'));
      setRoom(updatedRoom);
      console.log(`Room: Update ok with passwd, uuid=${room.uuid}, data=${JSON.stringify(updatedRoom)}`);
    } catch (error) {
      handleError(error);
    } finally {
      setRequesting(false);
    }
  }, [t, handleError, setRequesting, setRoom]);

  if (!room) return <Spinner animation="border" variant="primary" />;
  return <>
    <Accordion defaultActiveKey={['2', '3']}>
      <Accordion.Item eventKey="0">
        <Accordion.Header>{t('lr.room.nav')}</Accordion.Header>
        <Accordion.Body>
          <Button variant="link" onClick={() => {
            setRoomId(null);
            searchParams.delete('roomid'); setSearchParams(searchParams);
          }}>Back to Rooms</Button>
        </Accordion.Body>
      </Accordion.Item>
      <Accordion.Item eventKey="1">
        <Accordion.Header>{t('lr.room.rbasic')}</Accordion.Header>
        <Accordion.Body>
          {room ? <LiveRoomSettings {...{ room, requesting, updateRoom }} /> : ''}
        </Accordion.Body>
      </Accordion.Item>
      <Accordion.Item eventKey="2">
        <Accordion.Header>{t('lr.room.stream')}</Accordion.Header>
        <Accordion.Body>
          {room ? <LiveRoomStreamer {...{ room }} /> : ''}
        </Accordion.Body>
      </Accordion.Item>
      {room ? <Accordion.Item eventKey="3">
        <Accordion.Header>{t('lr.room.aiw')}</Accordion.Header>
        <Accordion.Body>
          <LiveRoomAssistant {...{ room, requesting, updateRoom }} />
        </Accordion.Body>
      </Accordion.Item> : ''}
    </Accordion>
  </>;
}

function LiveRoomSettings({ room, requesting, updateRoom }) {
  const { t } = useTranslation();
  const [name, setName] = React.useState(room.title);

  const onUpdateRoom = React.useCallback((e, room) => {
    e.preventDefault();
    updateRoom({
      ...room, title: name,
    });
  }, [name, updateRoom]);

  return (
    <Form>
      <Form.Group className="mb-3">
        <Form.Label>{t('lr.create.name')}</Form.Label>
        <Form.Text> * {t('lr.create.name2')}</Form.Text>
        <Form.Control as="input" defaultValue={name} onChange={(e) => setName(e.target.value)} />
      </Form.Group>
      <Button ariant="primary" type="submit" disabled={requesting} onClick={(e) => onUpdateRoom(e, room)}>
        {t('helper.update')}
      </Button>
    </Form>
  );
}

function LiveRoomStreamer({ room }) {
  const { t } = useTranslation();
  const env = React.useContext(SrsEnvContext)[0];

  const [urls, setUrls] = React.useState({});
  const [streamType, setStreamType] = React.useState('rtmp');

  const copyToClipboard = React.useCallback((e, text) => {
    e.preventDefault();

    Clipboard.copy(text).then(() => {
      alert(t('helper.copyOk'));
    }).catch((err) => {
      alert(`${t('helper.copyFail')} ${err}`);
    });
  }, [t]);

  const changeStreamType = React.useCallback((e, t) => {
    e.preventDefault();
    setStreamType(t);
  }, [setStreamType]);

  React.useEffect(() => {
    if (!room?.secret) return;
    const urls = buildUrls(`live/${room.stream}`, { publish: room.secret }, env);
    setUrls(urls);
  }, [room, env, setUrls]);

  const {
    rtmpServer, rtmpStreamKey, flvPlayer, flvUrl, hlsPlayer, m3u8Url, srtPublishUrl, rtcPlayer, whepUrl,
  } = urls;

  const watchKey = `${room.stream}&passwd=${room.passwd}`;

  return (
    <Card>
      <Card.Header>
        <Nav variant="tabs" defaultActiveKey="#rtmp">
          <Nav.Item>
            <Nav.Link href="#rtmp" onClick={(e) => changeStreamType(e, 'rtmp')}>{t('live.obs.title')}</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link href="#srt" onClick={(e) => changeStreamType(e, 'srt')}>{t('live.srt.title')}</Nav.Link>
          </Nav.Item>
        </Nav>
      </Card.Header>
      {streamType === 'rtmp' ? <Card.Body>
        <div>
          {t('live.obs.server')} <code>{rtmpServer}</code> &nbsp;
          <div role='button' style={{ display: 'inline-block' }} title={t('helper.copy')}>
            <Icon.Clipboard size={20} onClick={(e) => copyToClipboard(e, rtmpServer)} />
          </div>
        </div>
        <div>
          {t('live.obs.key')} <code>{rtmpStreamKey}</code> &nbsp;
          <div role='button' style={{ display: 'inline-block' }} title={t('helper.copy')}>
            <Icon.Clipboard size={20} onClick={(e) => copyToClipboard(e, rtmpStreamKey)} />
          </div>
        </div>
        <div>
          {'拉流密钥（观看密钥）'} <code>{watchKey}</code> &nbsp;
          <div role='button' style={{ display: 'inline-block' }} title={t('helper.copy')}>
            <Icon.Clipboard size={20} onClick={(e) => copyToClipboard(e, watchKey)} />
          </div>
        </div>
        <div>
          {t('live.share.flv')}&nbsp;
          <a href={flvPlayer} target='_blank' rel='noreferrer'>{t('live.share.simple')}</a>,&nbsp;
          <code>{flvUrl}</code> &nbsp;
          <div role='button' style={{ display: 'inline-block' }} title={t('helper.copy')}>
            <Icon.Clipboard size={20} onClick={(e) => copyToClipboard(e, flvUrl)} />
          </div>
        </div>
        <div>
          {t('live.share.hls')}&nbsp;
          <a href={hlsPlayer} target='_blank' rel='noreferrer'>{t('live.share.simple')}</a>,&nbsp;
          <code>{m3u8Url}</code> &nbsp;
          <div role='button' style={{ display: 'inline-block' }} title={t('helper.copy')}>
            <Icon.Clipboard size={20} onClick={(e) => copyToClipboard(e, m3u8Url)} />
          </div>
        </div>
        <div>
          {t('live.share.rtc')}&nbsp;
          <a href={rtcPlayer} target='_blank' rel='noreferrer'>{t('live.share.simple')}</a>&nbsp;
          <code>{whepUrl}</code> &nbsp;
          <div role='button' style={{ display: 'inline-block' }} title={t('helper.copy')}>
            <Icon.Clipboard size={20} onClick={(e) => copyToClipboard(e, whepUrl)} />
          </div>
        </div>
      </Card.Body> :
        <Card.Body>
          <div>
            {t('live.obs.server')} <code>{srtPublishUrl}</code> &nbsp;
            <div role='button' style={{ display: 'inline-block' }} title={t('helper.copy')}>
              <Icon.Clipboard size={20} onClick={(e) => copyToClipboard(e, srtPublishUrl)} />
            </div>
          </div>
          <div>
            {t('live.obs.key')} <code>{t('live.obs.nokey')}</code>
          </div>
          <div>
            {t('live.share.hls')}&nbsp;
            <a href={hlsPlayer} target='_blank' rel='noreferrer'>{t('live.share.simple')}</a>,&nbsp;
            <code>{m3u8Url}</code> &nbsp;
            <div role='button' style={{ display: 'inline-block' }} title={t('helper.copy')}>
              <Icon.Clipboard size={20} onClick={(e) => copyToClipboard(e, m3u8Url)} />
            </div>
          </div>
        </Card.Body>}
    </Card>
  );
}

function LiveRoomAssistant({ room, requesting, updateRoom }) {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const language = useSrsLanguage();

  const [aiName, setAiName] = React.useState(room.aiName);
  const [aiProvider, setAiProvider] = React.useState(room.aiProvider || 'openai');
  const [aiSecretKey, setAiSecretKey] = React.useState(room.aiSecretKey);
  const [aiOrganization, setAiOrganization] = React.useState(room.aiOrganization);
  const [aiBaseURL, setAiBaseURL] = React.useState(room.aiBaseURL || (language === 'zh' ? '' : 'https://api.openai.com/v1'));
  const [aiAsrEnabled, setAiAsrEnabled] = React.useState(room.aiAsrEnabled);
  const [aiChatEnabled, setAiChatEnabled] = React.useState(room.aiChatEnabled);
  const [aiPostEnabled, setAiPostEnabled] = React.useState(room.aiPostEnabled);
  const [aiTtsEnabled, setAiTtsEnabled] = React.useState(room.aiTtsEnabled);
  const [aiAsrLanguage, setAiAsrLanguage] = React.useState(room.aiAsrLanguage || language || 'en');
  const [aiAsrPrompt, setAiAsrPrompt] = React.useState(room.aiAsrPrompt || 'user-ai');
  const [aiChatModel, setAiChatModel] = React.useState(room.aiChatModel || 'gpt-3.5-turbo');
  const [aiChatPrompt, setAiChatPrompt] = React.useState(room.aiChatPrompt || 'You are a helpful assistant.');
  const [aiChatMaxWindow, setAiChatMaxWindow] = React.useState(room.aiChatMaxWindow || 5);
  const [aiChatMaxWords, setAiChatMaxWords] = React.useState(room.aiChatMaxWords || 300);
  const [aiPostModel, setAiPostModel] = React.useState(room.aiPostModel || 'gpt-3.5-turbo');
  const [aiPostPrompt, setAiPostPrompt] = React.useState(room.aiPostPrompt || 'You are a helpful assistant.');
  const [aiPostMaxWindow, setAiPostMaxWindow] = React.useState(room.aiPostMaxWindow || 5);
  const [aiPostMaxWords, setAiPostMaxWords] = React.useState(room.aiPostMaxWords || 300);

  const [configItem, setConfigItem] = React.useState('basic');
  const [userName, setUserName] = React.useState('You');
  const [userLanguage, setUserLanguage] = React.useState(room.aiAsrLanguage || language);
  const [aiPattern, setAiPattern] = React.useState('chat');
  const [assistantLink, setAssistantLink] = React.useState();

  React.useEffect(() => {
    if (aiSecretKey) return;

    axios.post('/terraform/v1/mgmt/openai/query', null, {
      headers: Token.loadBearerHeader(),
    }).then(res => {
      const data = res.data.data;
      if (!data) {
        console.log(`LiveRoom: Query open ai error, data=${JSON.stringify(data)}`);
        return;
      }
      setAiSecretKey(data.aiSecretKey);
      setAiBaseURL(data.aiBaseURL);
      setAiOrganization(data.aiOrganization);
      console.log(`LiveRoom: Query open ai ok, data=${JSON.stringify(data)}`);
    }).catch(handleError);
  }, [handleError, aiSecretKey, setAiSecretKey, setAiBaseURL, setAiOrganization]);

  const changeConfigItem = React.useCallback((e, t) => {
    e.preventDefault();
    setConfigItem(t);
  }, [setConfigItem]);

  const onUpdateRoom = React.useCallback((e) => {
    e.preventDefault();
    updateRoom({
      ...room, assistant: true,
      aiName, aiProvider, aiSecretKey, aiOrganization, aiBaseURL, aiAsrLanguage, aiChatModel,
      aiChatPrompt, aiChatMaxWindow: parseInt(aiChatMaxWindow),
      aiChatMaxWords: parseInt(aiChatMaxWords), aiAsrEnabled: !!aiAsrEnabled,
      aiChatEnabled: !!aiChatEnabled, aiTtsEnabled: !!aiTtsEnabled,
      aiAsrPrompt,
      aiPostEnabled: !!aiPostEnabled, aiPostModel, aiPostPrompt,
      aiPostMaxWindow: parseInt(aiPostMaxWindow), aiPostMaxWords: parseInt(aiPostMaxWords),
    })
  }, [
    updateRoom, room, aiName, aiProvider, aiSecretKey, aiBaseURL, aiAsrLanguage, aiChatModel, aiChatPrompt,
    aiChatMaxWindow, aiChatMaxWords, aiAsrEnabled, aiChatEnabled, aiTtsEnabled, aiAsrPrompt, aiOrganization,
    aiPostEnabled, aiPostModel, aiPostPrompt, aiPostMaxWindow, aiPostMaxWords,
  ]);

  const onDisableRoom = React.useCallback((e) => {
    e.preventDefault();
    updateRoom({ ...room, assistant: false });
  }, [updateRoom, room]);

  const generateAssistantLink = React.useCallback((e) => {
    e && e.preventDefault();

    const roomUUID = room.uuid;
    const roomToken = room.roomToken;
    if (!roomUUID) return;

    // For assistant link, we must set expire date.
    const params = [
      'app=ai-talk',
      'popout=1',
      'assistant=1',
      `created=${new Date().toISOString()}`,
      `random=${Math.random().toString(16).slice(-8)}`,
      ...(userName ? [`username=${userName}`] : []),
      ...(userLanguage ? [`language=${userLanguage}`] : []),
      ...(aiPattern ? [`pattern=${aiPattern}`] : []),
      `room=${roomUUID}`,
      `roomToken=${roomToken}`,
    ];
    const url = `${window.PUBLIC_URL}/${Locale.current()}/routers-popout?${params.join('&')}`;
    setAssistantLink(url);
    console.log(`Generated assistant URL: ${url}`);
  }, [setAssistantLink, room, userName, userLanguage, aiPattern]);

  // If data updated, update link.
  React.useEffect(() => {
    generateAssistantLink();
  }, [generateAssistantLink, userName, userLanguage, aiPattern]);

  if (!room.assistant) {
    return (
      <Button variant="primary" type="button" disabled={requesting}
        onClick={(e) => updateRoom({ ...room, assistant: true })}>
        {t('lr.room.enable')}
      </Button>
    );
  }
  return (
    <Form>
      <Card>
        <Card.Header>
          <Nav variant="tabs" defaultActiveKey="#basic">
            <Nav.Item>
              <Nav.Link href="#basic" onClick={(e) => changeConfigItem(e, 'basic')}>{t('lr.room.basic')}</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link href="#provider" onClick={(e) => changeConfigItem(e, 'provider')}>{t('lr.room.provider')}</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link href="#asr" onClick={(e) => changeConfigItem(e, 'asr')}>{t('lr.room.asr')}</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link href="#chat" onClick={(e) => changeConfigItem(e, 'chat')}>{t('lr.room.chat')}</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link href="#post" onClick={(e) => changeConfigItem(e, 'post')}>{t('lr.room.post')}</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link href="#tts" onClick={(e) => changeConfigItem(e, 'tts')}>{t('lr.room.tts')}</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link href="#assistant" onClick={(e) => changeConfigItem(e, 'assistant')}>{t('lr.room.assistant')}</Nav.Link>
            </Nav.Item>
          </Nav>
        </Card.Header>
        {configItem === 'basic' && <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>{t('lr.room.name')}</Form.Label>
            <Form.Text> * {t('lr.room.name2')}</Form.Text>
            <Form.Control as="input" type='input' defaultValue={aiName} onChange={(e) => setAiName(e.target.value)} />
          </Form.Group>
          <LiveRoomAssistantUpdateButtons {...{ requesting, onUpdateRoom, onDisableRoom }} />
        </Card.Body>}
        {configItem === 'provider' && <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>{t('lr.room.provider')}</Form.Label>
            <Form.Text> * {t('lr.room.provider2')}</Form.Text>
            <Form.Select defaultValue={aiProvider} onChange={(e) => setAiProvider(e.target.value)}>
              <option value="">--{t('helper.noSelect')}--</option>
              <option value="openai">OpenAI</option>
            </Form.Select>
          </Form.Group>
          <OpenAISecretSettings {...{
            baseURL: aiBaseURL, setBaseURL: setAiBaseURL,
            secretKey: aiSecretKey, setSecretKey: setAiSecretKey,
            organization: aiOrganization, setOrganization: setAiOrganization,
          }} />
          <p></p>
          <LiveRoomAssistantUpdateButtons {...{ requesting, onUpdateRoom, onDisableRoom }} />
        </Card.Body>}
        {configItem === 'asr' && <Card.Body>
          <Form.Group className="mb-3">
            <Form.Group className="mb-3" controlId="formAiAsrEnabledCheckbox">
              <Form.Check type="checkbox" label={t('lr.room.asre')} defaultChecked={aiAsrEnabled} onClick={() => setAiAsrEnabled(!aiAsrEnabled)} />
            </Form.Group>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('transcript.lang')}</Form.Label>
            <Form.Text> * {t('transcript.lang2')}. &nbsp;
              {t('helper.eg')} <code>en, zh, fr, de, ja, ru </code>, ... &nbsp;
              {t('helper.see')} <a href='https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes' target='_blank'
                rel='noreferrer'>ISO-639-1</a>.
            </Form.Text>
            <Form.Control as="input" defaultValue={aiAsrLanguage} onChange={(e) => setAiAsrLanguage(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('lr.room.asrp')}</Form.Label>
            <Form.Text> * {t('lr.room.asrp2')}.</Form.Text>
            <Form.Select defaultValue={aiAsrPrompt} onChange={(e) => setAiAsrPrompt(e.target.value)}>
              <option value="">--{t('helper.noSelect')}--</option>
              <option value="user-only">User Input</option>
              <option value="user-ai">User Input + AI Output</option>
            </Form.Select>
          </Form.Group>
          <LiveRoomAssistantUpdateButtons {...{ requesting, onUpdateRoom, onDisableRoom }} />
        </Card.Body>}
        {configItem === 'chat' && <Card.Body>
          <Form.Group className="mb-3">
            <Form.Group className="mb-3" controlId="formAiChatEnabledCheckbox">
              <Form.Check type="checkbox" label={t('lr.room.chate')} defaultChecked={aiChatEnabled} onClick={() => setAiChatEnabled(!aiChatEnabled)} />
            </Form.Group>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('lr.room.model')}</Form.Label>
            <Form.Text> * {t('lr.room.model2')}</Form.Text>
            <Form.Control as="input" type='input' defaultValue={aiChatModel} onChange={(e) => setAiChatModel(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('lr.room.prompt')}</Form.Label>
            <Form.Text> * {t('lr.room.prompt2')}</Form.Text>
            <Form.Control as="textarea" type='text' rows={7} defaultValue={aiChatPrompt} onChange={(e) => setAiChatPrompt(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('lr.room.window')}</Form.Label>
            <Form.Text> * {t('lr.room.window2')}</Form.Text>
            <Form.Control as="input" type='input' defaultValue={aiChatMaxWindow} onChange={(e) => setAiChatMaxWindow(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('lr.room.words')}</Form.Label>
            <Form.Text> * {t('lr.room.words2')}</Form.Text>
            <Form.Control as="input" type='input' defaultValue={aiChatMaxWords} onChange={(e) => setAiChatMaxWords(e.target.value)} />
          </Form.Group>
          <LiveRoomAssistantUpdateButtons {...{ requesting, onUpdateRoom, onDisableRoom }} />
        </Card.Body>}
        {configItem === 'post' && <Card.Body>
          <Form.Group className="mb-3">
            <Form.Group className="mb-3" controlId="formAiChatEnabledCheckbox">
              <Form.Check type="checkbox" label={t('lr.room.chate')} defaultChecked={aiPostEnabled} onClick={() => setAiPostEnabled(!aiPostEnabled)} />
            </Form.Group>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('lr.room.model')}</Form.Label>
            <Form.Text> * {t('lr.room.model2')}</Form.Text>
            <Form.Control as="input" type='input' defaultValue={aiPostModel} onChange={(e) => setAiPostModel(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('lr.room.prompt')}</Form.Label>
            <Form.Text> * {t('lr.room.prompt2')}</Form.Text>
            <Form.Control as="textarea" type='text' rows={7} defaultValue={aiPostPrompt} onChange={(e) => setAiPostPrompt(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('lr.room.window')}</Form.Label>
            <Form.Text> * {t('lr.room.window2')}</Form.Text>
            <Form.Control as="input" type='input' defaultValue={aiPostMaxWindow} onChange={(e) => setAiPostMaxWindow(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('lr.room.words')}</Form.Label>
            <Form.Text> * {t('lr.room.words2')}</Form.Text>
            <Form.Control as="input" type='input' defaultValue={aiPostMaxWords} onChange={(e) => setAiPostMaxWords(e.target.value)} />
          </Form.Group>
          <LiveRoomAssistantUpdateButtons {...{ requesting, onUpdateRoom, onDisableRoom }} />
        </Card.Body>}
        {configItem === 'tts' && <Card.Body>
          <Form.Group className="mb-3">
            <Form.Group className="mb-3" controlId="formAiTtsEnabledCheckbox">
              <Form.Check type="checkbox" label={t('lr.room.ttse')} defaultChecked={aiTtsEnabled} onClick={() => setAiTtsEnabled(!aiTtsEnabled)} />
            </Form.Group>
          </Form.Group>
          <LiveRoomAssistantUpdateButtons {...{ requesting, onUpdateRoom, onDisableRoom }} />
        </Card.Body>}
        {configItem === 'assistant' && <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>{t('lr.room.uname')}</Form.Label>
            <Form.Text> * {t('lr.room.uname2')}</Form.Text>
            <Form.Control as="input" type='input' defaultValue={userName} onChange={(e) => {
              e.preventDefault();
              setUserName(e.target.value);
            }} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('transcript.lang')}</Form.Label>
            <Form.Text> * {t('transcript.lang3')}. &nbsp;
              {t('helper.eg')} <code>en, zh, fr, de, ja, ru </code>, ... &nbsp;
              {t('helper.see')} <a href='https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes' target='_blank' rel='noreferrer'>ISO-639-1</a>.
            </Form.Text>
            <Form.Control as="input" defaultValue={userLanguage} onChange={(e) => {
              e.preventDefault();
              setUserLanguage(e.target.value);
            }} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('lr.room.pattern')}</Form.Label>
            <Form.Text> * {t('lr.room.pattern2')}.</Form.Text>
            <Form.Select defaultValue={aiPattern} onChange={(e) => setAiPattern(e.target.value)}>
              <option value="">--{t('helper.noSelect')}--</option>
              <option value="chat">Chat ({t('lr.room.patternChat')})</option>
              <option value="dictation">Dictation ({t('lr.room.patternListen')})</option>
            </Form.Select>
          </Form.Group>
          <Button variant="primary" type="button" onClick={generateAssistantLink}>
            {t('helper.generate')}
          </Button>
          <p></p>
          {assistantLink && <p>
            Assistant: <a href={assistantLink} target='_blank' rel='noreferrer'>{userName} {userLanguage} {aiPattern}</a>
          </p>}
        </Card.Body>}
      </Card>
    </Form>
  );
}

function LiveRoomAssistantUpdateButtons({ requesting, onUpdateRoom, onDisableRoom }) {
  const { t } = useTranslation();

  return <>
    <Button variant="primary" type="button" disabled={requesting} onClick={onUpdateRoom}>
      {t('lr.room.update')}
    </Button> &nbsp;
    <Button variant="primary" type="button" disabled={requesting} onClick={onDisableRoom}>
      {t('lr.room.disable')}
    </Button>
  </>;
}